/*
 * Behaviour for the services field in blocks/custom-lead-form.liquid: the
 * caret opens a panel of the services still on offer, picking one turns it into
 * a chip in the field and closes the panel again, and dropping a chip puts that
 * service back on offer.
 *
 * A service is therefore in exactly one of two places at a time — the panel or
 * the field — which is what stops the same one being picked twice and is why
 * nothing in the panel is ever drawn in a chosen state.
 *
 * Everything it touches is already in the page — the chips, the panel, the
 * options and their labels are all server-rendered by the block, chips simply
 * `hidden` until chosen. So this file flips attributes and never builds markup,
 * which keeps every string a visitor or a screen reader hears inside the locale
 * files where the rest of the form's copy lives.
 *
 * Two controls carry `contact[services]` in the markup and exactly one of them
 * is live at a time: the native `<select multiple>`, which is what a visitor
 * without this script gets, and the hidden input, which posts the chosen
 * services as one comma-joined line. Upgrading swaps them — name off the
 * select, `disabled` off the input — so the field can neither post twice nor
 * post nothing if this module fails to load.
 *
 * The native select stays in the DOM as the selection's store rather than being
 * thrown away: it is what a browser restores on a back-navigation, so reading
 * the selection back off it beats keeping a second list in sync with it.
 */
class CustomMultiSelect extends HTMLElement {
  /** @type {HTMLSelectElement | null} */
  #native = null;

  /** @type {HTMLInputElement | null} */
  #value = null;

  /** @type {HTMLButtonElement | null} */
  #toggle = null;

  /** @type {HTMLElement | null} */
  #list = null;

  /*
   * Panel options, native options and chips are all rendered from one Liquid
   * loop over the same services, so a position means the same service in all
   * three and no lookup by value is needed.
   */

  /** @type {HTMLElement[]} */
  #options = [];

  /** @type {HTMLElement[]} */
  #chips = [];

  connectedCallback() {
    this.#native = this.querySelector('[data-ref="native"]');
    this.#value = this.querySelector('[data-ref="value"]');
    this.#toggle = this.querySelector('[data-ref="toggle"]');
    this.#list = this.querySelector('[data-ref="list"]');
    this.#options = Array.from(this.querySelectorAll('[role="option"]'));
    this.#chips = Array.from(this.querySelectorAll('.custom-lead-form__chip'));

    if (!this.#native || !this.#value || !this.#toggle || !this.#list) return;

    // Take the field over from the native select. This runs again every time
    // the theme editor re-inserts the block, so both halves have to be safe to
    // repeat.
    this.#native.removeAttribute('name');
    this.#value.disabled = false;

    this.addEventListener('mousedown', this.#handleMousedown);
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('focusout', this.#handleFocusout);

    this.#sync();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#handleDocumentClick);
  }

  get #expanded() {
    return this.#toggle?.getAttribute('aria-expanded') === 'true';
  }

  /**
   * Safari does not focus an option or a button on click, so left alone the
   * panel would lose focus to the page and close on the first pick. Refusing
   * the default keeps focus where it is; the click handler then moves it
   * deliberately.
   *
   * @param {MouseEvent} event
   */
  #handleMousedown = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('[role="option"], .custom-lead-form__chip-remove, [data-ref="clear"]')) {
      event.preventDefault();
    }
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-ref="toggle"]')) {
      if (this.#expanded) this.#close();
      else this.#open();
      return;
    }

    if (target.closest('[data-ref="clear"]')) {
      this.#options.forEach((_option, index) => this.#select(index, false));
      this.#sync();
      this.#toggle?.focus();
      return;
    }

    const chip = target.closest('.custom-lead-form__chip');
    if (chip instanceof HTMLElement && target.closest('.custom-lead-form__chip-remove')) {
      this.#remove(this.#chips.indexOf(chip));
      return;
    }

    const option = target.closest('[role="option"]');
    if (option instanceof HTMLElement) {
      this.#choose(this.#options.indexOf(option));
      return;
    }

    // The placeholder and the space the chips leave are not controls, but they
    // read as the field, so a click there does what the caret does.
    if (target.closest('.custom-lead-form__select-wrapper') && !this.#expanded) {
      this.#open();
    }
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (event.key === 'Escape' && this.#expanded) {
      event.preventDefault();
      this.#close();
      this.#toggle?.focus();
      return;
    }

    if (target.closest('[data-ref="toggle"]')) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.#open(event.key === 'ArrowUp' ? 'last' : 'first');
      }
      return;
    }

    const option = target.closest('[role="option"]');
    if (!(option instanceof HTMLElement)) return;

    // Arrows walk what is on offer, not the full list — the services already
    // chosen are not in the panel to be walked past.
    const offered = this.#offeredOptions();
    const position = offered.indexOf(option);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        offered[position + 1]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        offered[position - 1]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        offered[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        offered[offered.length - 1]?.focus();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.#choose(this.#options.indexOf(option));
        break;
      case 'Tab':
        this.#close();
        break;
      default:
        break;
    }
  };

  /*
   * Read on the next frame rather than from `relatedTarget`, which is null for
   * plenty of ordinary focus moves and would close the panel under a visitor
   * who is still using it.
   */
  #handleFocusout = () => {
    requestAnimationFrame(() => {
      if (!this.contains(document.activeElement)) this.#close();
    });
  };

  /** @param {MouseEvent} event */
  #handleDocumentClick = (event) => {
    const target = event.target;
    if (target instanceof Node && this.contains(target)) return;
    this.#close();
  };

  /** @param {'first' | 'last'} [from] */
  #open(from = 'first') {
    if (!this.#list || !this.#toggle) return;

    const offered = this.#offeredOptions();
    if (offered.length === 0) return;

    this.#list.hidden = false;
    this.#toggle.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.#handleDocumentClick);

    (from === 'last' ? offered[offered.length - 1] : offered[0])?.focus();
  }

  #close() {
    if (!this.#list || !this.#toggle || !this.#expanded) return;

    this.#list.hidden = true;
    this.#toggle.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.#handleDocumentClick);
  }

  /**
   * Picking is a one-shot: the service moves to the field and the panel has
   * done its job, so it closes and hands focus back to the caret it came from.
   *
   * @param {number} index
   */
  #choose(index) {
    this.#select(index, true);
    this.#sync();
    this.#close();

    // Taking the last service on offer leaves the caret disabled, and focus
    // cannot be handed to that — the clear button is what is still there.
    if (this.#toggle && !this.#toggle.disabled) this.#toggle.focus();
    else this.querySelector('[data-ref="clear"]')?.focus();
  }

  /**
   * @param {number} index
   * @param {boolean} selected
   */
  #select(index, selected) {
    const option = this.#options[index];
    if (!option) return;

    // Out of the panel and into the field, or the other way about.
    option.hidden = selected;

    const native = this.#native?.options[index];
    if (native) native.selected = selected;

    const chip = this.#chips[index];
    if (chip) chip.hidden = !selected;
  }

  /** @returns {HTMLElement[]} The services still on offer, in panel order. */
  #offeredOptions() {
    return this.#options.filter((option) => !option.hidden);
  }

  /**
   * Dropping a chip takes its remove button out of the tab order with focus
   * still on it, so focus moves on deliberately: to the chip that slid into its
   * place, else the last one left, else back to the field.
   *
   * @param {number} index
   */
  #remove(index) {
    const position = this.#visibleChips().indexOf(this.#chips[index]);

    this.#select(index, false);
    this.#sync();

    const remaining = this.#visibleChips();
    const next = remaining[position] ?? remaining[remaining.length - 1];
    const target = next?.querySelector('.custom-lead-form__chip-remove');

    if (target instanceof HTMLElement) target.focus();
    else this.#toggle?.focus();
  }

  /** @returns {HTMLElement[]} */
  #visibleChips() {
    return this.#chips.filter((chip) => !chip.hidden);
  }

  /** Writes the selection back to the field: what posts, and what shows. */
  #sync() {
    const chosen = this.#native ? Array.from(this.#native.selectedOptions) : [];
    const values = chosen.map((option) => option.value);

    if (this.#value) this.#value.value = values.join(', ');

    this.#setHidden('[data-ref="chips"]', values.length === 0);
    this.#setHidden('[data-ref="placeholder"]', values.length > 0);
    this.#setHidden('[data-ref="clear"]', values.length === 0);

    // With every service in the field there is nothing left to open onto.
    if (this.#toggle) this.#toggle.disabled = this.#offeredOptions().length === 0;
  }

  /**
   * @param {string} selector
   * @param {boolean} hidden
   */
  #setHidden(selector, hidden) {
    const element = this.querySelector(selector);
    if (element instanceof HTMLElement) element.hidden = hidden;
  }
}

if (!customElements.get('custom-multi-select')) {
  customElements.define('custom-multi-select', CustomMultiSelect);
}

/*
 * Every lead form on the page posts to the same endpoint, and Liquid renders
 * them all from the same `form` object — so one submit draws the same "thanks"
 * in the hero form and in the footer form at once. Nothing server-side can tell
 * them apart: `form.id` is whatever that instance passed in, so it always
 * matches itself.
 *
 * What does tell them apart is where the visitor landed. The form tag renders
 * `action="/contact#<the form's id>"`, so the fragment names the one that was
 * submitted; the rest drop their notice. With no fragment there is nothing to
 * go on, and every notice stands, which is where this started.
 */
function scopePostedNotice() {
  const submitted = decodeURIComponent(location.hash.slice(1));
  if (!submitted) return;

  const notices = document.querySelectorAll('.custom-lead-form__success, .custom-lead-form__error');
  if (notices.length < 2) return;

  for (const notice of notices) {
    const form = notice.closest('form');
    if (!form || form.id === submitted) continue;

    // The error notice is an input's `aria-describedby` target, so the pointer
    // to it has to go with it. Only the error notice carries an id.
    if (notice.id) {
      for (const field of form.querySelectorAll(`[aria-describedby="${CSS.escape(notice.id)}"]`)) {
        field.removeAttribute('aria-describedby');
        field.removeAttribute('aria-invalid');
      }
    }

    notice.remove();
  }

  /*
   * `autofocus` lands on the first one in the document, which is rarely the one
   * that was submitted, and removing it would drop focus on the body.
   */
  const survivor = document.querySelector('.custom-lead-form__success, .custom-lead-form__error');
  if (survivor instanceof HTMLElement) survivor.focus();
}

scopePostedNotice();

const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/events';
const KLAVIYO_REVISION = '2026-07-15';
const KLAVIYO_METRIC = 'Lead Form Submitted';

/*
 * Shopify only emails a contact form and keeps nothing, so each submission is
 * also logged to Klaviyo as an event — that is the lead history. The submit is
 * held until Klaviyo answers (or two seconds pass) because the native post
 * navigates away and would cancel a request still in flight; `keepalive` can't
 * stand in, as browsers won't preflight it and this call needs a preflight.
 *
 * Shopify's captcha bootstrap also listens for submit on `document`: it cancels
 * the first one and later posts with `form.submit()`, which fires no event. So
 * this listens on `window` in capture, ahead of it, holds the submit back from
 * it, and replays it with `requestSubmit()` so the captcha still gets its turn.
 */
function logLeadToKlaviyo(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (!form.matches('.custom-lead-form__form') || form.dataset.klaviyoSent) return;

  const key = form.dataset.klaviyoKey?.trim();
  if (!key || window.Shopify?.designMode) return;

  const data = new FormData(form);
  const field = (name) => String(data.get(`contact[${name}]`) ?? '').trim();

  const email = field('email');
  if (!email) return;

  event.preventDefault();
  event.stopPropagation();
  form.dataset.klaviyoSent = 'true';

  const [firstName = '', ...rest] = field('name').split(/\s+/);
  const services = data
    .getAll('contact[services]')
    .flatMap((value) => String(value).split(','))
    .map((value) => value.trim())
    .filter(Boolean);

  const body = {
    data: {
      type: 'event',
      attributes: {
        unique_id: crypto.randomUUID(),
        properties: {
          name: field('name'),
          email,
          company: field('company'),
          phone: field('phone'),
          industry: field('industry'),
          services,
          form: field('id'),
          page_url: location.href,
          page_title: document.title,
        },
        metric: { data: { type: 'metric', attributes: { name: KLAVIYO_METRIC } } },
        profile: {
          data: {
            type: 'profile',
            // Phone stays a custom property: Klaviyo rejects the whole event
            // when `phone_number` isn't strict E.164, and visitors type it freely.
            attributes: {
              email,
              first_name: firstName,
              last_name: rest.join(' '),
              organization: field('company'),
              properties: { lead_phone: field('phone'), industry: field('industry') },
            },
          },
        },
      },
    },
  };

  fetch(`${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(key)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(2000),
  })
    .catch(() => {})
    .finally(() => {
      // The replayed submit is dispatched synchronously, so the flag only has
      // to outlive this call — clearing it keeps a back-navigated form logging.
      form.requestSubmit(event.submitter);
      delete form.dataset.klaviyoSent;
    });
}

window.addEventListener('submit', logLeadToKlaviyo, true);
